import React, { useState, useEffect, useMemo } from 'react'
import { Box, Divider, Typography, Select, MenuItem, TextField, InputAdornment, FormControl, Button, Checkbox, Chip, Menu } from '@mui/material'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { IUser, IBulkEligibilityPayload, useChangeBulkEligibilityMutationById, UserStatusEnum } from '@/@data'
import dayjs from 'dayjs'
import { UserAvatar } from '../common/UserAvatar'
// import { useRowSelection } from '@/@core/hooks'
import { useNotification, useRouteQuery, useTranslator } from "@/hooks"

interface TransactionDetailTableEmployerProps {
  data: IUser[]
  meta: unknown
  companyId: any
  onRowClick?: (employee: IUser) => void
  onEdit?: (employee: IUser) => void
  refetch?: () => void
  isDetail?: boolean
  onSuccess: () => void
  onExposeSubmit?: (submitFn: () => void) => void
}

export const TransactionDetailTableEmployer: React.FC<TransactionDetailTableEmployerProps> = ({
  data,
  meta,
  companyId,
  // onEdit,
  refetch,
  onExposeSubmit,
}) => {
  const { __ } = useTranslator()
  const ALLOWED_STATUSES = useMemo(() => new Set(['pending', 'active', 'disable', 'blocked']), [])

  // Filter by eligibility_status first, then exclude guest statuses by user status
  const filteredData = useMemo(() => {
    const base = (data || []).filter(u => ALLOWED_STATUSES.has(String(u?.eligibility_status || '').toLowerCase()))

    return base.filter(u => ![UserStatusEnum.GUEST, UserStatusEnum.GUEST_WAITING].includes(u?.status as any))
  }, [data, ALLOWED_STATUSES])

  // Checkbox selection (align with TransactionTableList behavior)
  const [isAllChecked, setIsAllChecked] = useState<boolean>(false)
  const [excludedUserIds, setExcludedUserIds] = useState<Set<number>>(new Set())
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set())
  const bulkEligibilityMutation = useChangeBulkEligibilityMutationById()
  const changeBulkEligibilityById = bulkEligibilityMutation.mutateAsync
  const n = useNotification()
  const { searchQuery } = useRouteQuery()

  // Eligibility rate controls (toolbar)
  const PREDEFINED_RATES = [60, 70, 80]
  const [rateType, setRateType] = useState<'predefined' | 'custom'>('predefined')
  const [selectedRate, setSelectedRate] = useState<number>(60)
  const [customRate, setCustomRate] = useState<number | ''>('')

  // Individual cell editing states
  const [editingRow, setEditingRow] = useState<string | null>(null)
  const [editRateType, setEditRateType] = useState<'predefined' | 'custom'>('predefined')
  const [editSelectedRate, setEditSelectedRate] = useState<number>(60)
  const [editCustomRate, setEditCustomRate] = useState<number | ''>('')

  // Selection toolbar dropdown
  const [selectionAnchorEl, setSelectionAnchorEl] = useState<null | HTMLElement>(null)
  const selectionOpen = Boolean(selectionAnchorEl)

  const effectiveRate = rateType === 'predefined' ? selectedRate : Number(customRate || 0)

  // Expose submit function to parent
  useEffect(() => {
    if (onExposeSubmit) {
      onExposeSubmit(handleSubmit)
    }
  }, [onExposeSubmit])

  // Header checkbox: select all / unselect all
  const handleCheckAll = (checked: boolean) => {
    if (checked) {
      setIsAllChecked(true)
      setExcludedUserIds(new Set())
      setSelectedUserIds(new Set())
    } else {
      setIsAllChecked(false)
      setExcludedUserIds(new Set())
      setSelectedUserIds(new Set())
    }
  }

  // Individual checkbox
  const handleIndividualCheck = (id: number, checked: boolean) => {
    if (isAllChecked) {
      const nextExcluded = new Set(excludedUserIds)

      if (!checked) {
        nextExcluded.add(id)
      } else {
        nextExcluded.delete(id)
      }

      // If exclusions cover the whole dataset, exit select-all mode
      const totalCount = (meta as any)?.total || 0

      if (totalCount > 0 && nextExcluded.size >= totalCount) {
        setIsAllChecked(false)
        setExcludedUserIds(new Set())
        setSelectedUserIds(new Set())
      } else {
        setExcludedUserIds(nextExcluded)
      }
    } else {
      const nextSelected = new Set(selectedUserIds)

      if (checked) {
        nextSelected.add(id)
      } else {
        nextSelected.delete(id)
      }

      setSelectedUserIds(nextSelected)
    }
  }

  const getSelectedCount = () => {
    if (isAllChecked) {
      const totalCount = (meta as any)?.total || 0

      return totalCount - excludedUserIds.size
    }

    return selectedUserIds.size
  }

  // Handle functions for individual cell editing
  const handleEditClick = (rowId: string, currentRate: number) => {
    setEditingRow(rowId)
    setEditSelectedRate(currentRate)
    setEditRateType('predefined')
    setEditCustomRate('')
  }

  const handleEditSave = async (rowId: string) => {
    const newRate = editRateType === 'predefined' ? editSelectedRate : Number(editCustomRate || 0)

    try {
      await changeBulkEligibilityById({
        id: +companyId,
        payload: {
          is_all: 0,
          user_ids: [Number(rowId)],
          exclude_user_ids: [],
          eligibility_rate: newRate
        }
      })
      n.success(__('Eligibility rate updated successfully'))
      setEditingRow(null)
      refetch && refetch()
    } catch (err) {
      n.error(__('Update eligibility rate failed'))
    }
  }

  const handleEditCancel = () => {
    setEditingRow(null)
    setEditRateType('predefined')
    setEditCustomRate('')
  }

  const handleEditRateChange = (value: number | 'custom') => {
    if (value === 'custom') {
      setEditRateType('custom')
    } else {
      setEditRateType('predefined')
      setEditSelectedRate(value)
    }
  }

  const handleEditCustomRateChange = (value: string) => {
    const val = Number(value)

    if (Number.isNaN(val)) {
      setEditCustomRate('')
    } else {
      const bounded = Math.max(0, Math.min(100, val))

      setEditCustomRate(bounded)
    }
  }

  const handleEditBackToPredefined = () => {
    setEditRateType('predefined')
    setEditCustomRate('')
  }

  // const handleEditClearCustom = () => {
  //   setEditCustomRate('')
  // }

  // Build payload for bulk eligibility update
  const buildBulkEligibilityPayload = (): IBulkEligibilityPayload => {
    if (isAllChecked) {
      return {
        is_all: 1,
        user_ids: [],
        exclude_user_ids: Array.from(excludedUserIds),
        eligibility_rate: effectiveRate
      }
    }

    return {
      is_all: 0,
      user_ids: Array.from(selectedUserIds),
      exclude_user_ids: [],
      eligibility_rate: effectiveRate
    }
  }

  const handleSubmit = async () => {
    const count = getSelectedCount()

    if (count === 0) {
      n.error(__('Please select employees'))

      return

    }

    const payload = buildBulkEligibilityPayload()

    bulkEligibilityMutation.mutate(
      { id: +companyId, payload },
      {
        onSuccess: () => {
          n.success(__('Eligibility rate updated successfully'))
          setSelectedUserIds(new Set())
          setExcludedUserIds(new Set())
          setIsAllChecked(false)
          refetch && refetch()
        },
        onError: () => {
          n.error(__('Update eligibility rate failed'))
        }
      }
    )
  }

  const STATUS_LABELS = {
    [UserStatusEnum.ACTIVE]: __('Active'),
    [UserStatusEnum.INACTIVE]: __('Inactive'),
    [UserStatusEnum.DISABLE]: __('Disabled'),
    [UserStatusEnum.BLOCKED]: __('Blocked'),
    [UserStatusEnum.WAITING]: __('Pending'),
    [UserStatusEnum.WAITING_DELETE]: __('Waiting For Deletion'),
    [UserStatusEnum.GUEST]: __('Guest'),
    [UserStatusEnum.GUEST_WAITING]: __('Guest Waiting')
  } as const

  return (
    <>
      {getSelectedCount() > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px'
            }}
          >

            <Box>
              <div className='flex items-end border-gray-300'>
                <div>
                  <Button
                    variant='outlined'
                    color='secondary'
                    endIcon={<i className='tabler-chevron-down text-gray-600' />}
                    onClick={e => setSelectionAnchorEl(e.currentTarget)}
                    className='mr-2 rounded-full'
                    sx={{
                      borderRadius: '20px',
                      borderColor: '#d0d0d0',
                      color: '#666',
                      textTransform: 'none',
                      '&:hover': { borderColor: '#b0b0b0' }
                    }}
                  >
                    <span className='text-sm font-medium'>
                      {getSelectedCount()} {__('Selected')}
                    </span>
                  </Button>

                  <Menu
                    anchorEl={selectionAnchorEl}
                    open={selectionOpen}
                    onClose={() => setSelectionAnchorEl(null)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    sx={{ mt: 1 }}
                  >
                    <MenuItem
                      onClick={() => {
                        handleCheckAll(true)
                        setSelectionAnchorEl(null)
                      }}
                      sx={{ fontSize: '14px' }}
                    >
                      {(meta as any)?.total || 0} {__('Select all')}
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        handleCheckAll(false)
                        setSelectionAnchorEl(null)
                      }}
                      sx={{ fontSize: '14px' }}
                    >
                      {__('Unselect all')}
                    </MenuItem>
                  </Menu>
                </div>

                <div>
                  <Box>
                    <Box className='flex items-center gap-2'>
                      <FormControl size='medium'>
                        {rateType !== 'custom' ? (
                          <Select
                            value={selectedRate}
                            onChange={(e) => {
                              const value = e.target.value as number | 'custom'

                              if (value === 'custom') {
                                setRateType('custom')
                              } else {
                                setRateType('predefined')
                                setSelectedRate(value)
                              }
                            }}
                            displayEmpty
                            sx={{ minWidth: 110, height: 38, borderRadius: '20px' }}
                          >
                            {PREDEFINED_RATES.map(rate => (
                              <MenuItem key={rate} value={rate}>{rate} %</MenuItem>
                            ))}
                            <MenuItem value={'custom'}>{__('Custom')}</MenuItem>
                          </Select>
                        ) : (
                          <TextField
                            autoFocus
                            value={customRate}
                            onChange={e => {
                              const val = Number(e.target.value)

                              if (Number.isNaN(val)) {
                                setCustomRate('')
                              } else {
                                const bounded = Math.max(0, Math.min(100, val))

                                setCustomRate(bounded)
                              }
                            }}
                            size='small'
                            type='number'
                            placeholder='0'
                            className='rounded-lg w-[140px] h-[38px]'
                            sx={{
                              '& .MuiInputBase-root': { borderRadius: '20px' },
                              '& input[type=number]': {
                                '-moz-appearance': 'textfield'
                              },
                              '& input[type=number]::-webkit-outer-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0
                              },
                              '& input[type=number]::-webkit-inner-spin-button': {
                                '-webkit-appearance': 'none',
                                margin: 0
                              }
                            }}
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    <Button
                                      variant='text'
                                      size='small'
                                      onClick={() => {
                                        setRateType('predefined')
                                        setCustomRate('')
                                      }}
                                      sx={{
                                        minWidth: 'auto',
                                        padding: '2px 4px',
                                        height: '24px',
                                        borderRadius: '12px'
                                      }}
                                    >
                                      <i className='tabler-arrow-left text-xs' />
                                    </Button>
                                    <Button
                                      variant='text'
                                      size='small'
                                      onClick={() => setCustomRate('')}
                                      sx={{
                                        minWidth: 'auto',
                                        padding: '2px 4px',
                                        height: '24px',
                                        borderRadius: '12px'
                                      }}
                                    >
                                      <i
                                        className='tabler-x text-xs'
                                        style={{
                                          color: '#666'
                                        }}
                                      />
                                    </Button>
                                  </InputAdornment>
                                )
                              }
                            }}
                          />
                        )}
                      </FormControl>
                    </Box>
                  </Box>
                </div>

              </div>
            </Box>

            <Box className='flex items-center gap-2'>
              <Button
                variant='contained'
                color='primary'
                onClick={handleSubmit}
                startIcon={<i className='tabler-check' />}
                className='rounded-full'
              >
                {__('Submit')}
              </Button>
              <Button
                variant='tonal'
                color='secondary'
                onClick={() => {
                  setSelectedUserIds(new Set())
                  setExcludedUserIds(new Set())
                  setIsAllChecked(false)
                }}
                startIcon={<i className='tabler-x' />}
                className='rounded-full'
              >
                {__('Clear')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      <Box sx={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            border: '1px solid #e0e0e0',
            tableLayout: 'fixed'
          }}
        >
          <thead>
            <tr className='border-right-custom'>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  width: 60
                }}
              >
                <Checkbox
                  checked={isAllChecked}
                  indeterminate={!isAllChecked && selectedUserIds.size > 0}
                  onChange={e => handleCheckAll(e.target.checked)}
                />
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  width: 280
                }}
              >
                {__('Employees')}
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  width: 200
                }}
              >
                {__('Eligibility Rate')}
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  width: 150
                }}
              >
                {__('D.O.B')}
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  width: 150
                }}
              >
                {__('last update')}
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  width: 150
                }}
              >
                {__('Joined Date')}
              </th>
              <th
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  borderBottom: '1px solid #e0e0e0',
                  fontWeight: 500,
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  width: 180
                }}
              >
                {__('Status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {(filteredData || []).length === 0 && (
              <tr>
                <td colSpan={7} style={{ padding: 16, textAlign: 'center' }}>
                  <Typography className='text-[14px] font-normal text-[#2F2B3D]'>{__('No Data')}</Typography>
                </td>
              </tr>
            )}
            {(filteredData || []).map((row: IUser) => {
              const id = String(row?.id)
              const isEditing = editingRow === id

              return (
                <tr key={id}>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <Checkbox
                      size='small'
                      checked={
                        isAllChecked
                          ? !excludedUserIds.has(Number(id))
                          : selectedUserIds.has(Number(id))
                      }
                      onChange={e => handleIndividualCheck(Number(id), e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <UserAvatar user={row} link={`/cms/users/employee/${row.id}`} />
                  </td>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    {isEditing ? (
                      <Box className='flex items-center gap-1'>
                        {editRateType !== 'custom' ? (
                          <>
                            <div className='flex items-center border w-full bg-[#fff] rounded-full overflow-hidden'>
                              <TextField
                                select
                                value={editSelectedRate}
                                onChange={(e) => handleEditRateChange(e.target.value as number | 'custom')}
                                size='small'
                                className='w-full h-[40px]'
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    height: '40px',
                                    borderRadius: '8px',
                                    backgroundColor: '#fff',
                                    '& fieldset': {
                                      border: 'none !important',
                                    },
                                    '&:hover fieldset': {
                                      border: 'none !important',
                                    },
                                    '&.Mui-focused fieldset': {
                                      border: 'none !important',
                                    },
                                    '&.Mui-focused': {
                                      boxShadow: 'none !important',
                                      outline: 'none !important',
                                      backgroundColor: '#fff',
                                    },
                                  },
                                  '& .MuiSelect-select': {
                                    padding: '8px 12px',
                                  },
                                }}
                              >
                                {PREDEFINED_RATES.map(rate => (
                                  <MenuItem key={rate} value={rate}>{rate}%</MenuItem>
                                ))}
                                <MenuItem value={'custom'}>{__('Custom')}</MenuItem>
                              </TextField>
                              <div className='pr-2 flex'>
                                <Button
                                  size='small'
                                  onClick={() => handleEditSave(id)}
                                  sx={{
                                    minWidth: 'auto',
                                    padding: '2px 4px',
                                    height: '20px',
                                    fontSize: '10px',
                                    '& .tabler-square-check': {
                                      color: '#28C76F !important'
                                    }
                                  }}
                                >
                                  <i className='tabler-square-check text-xl' />
                                </Button>
                                <Button
                                  color='error'
                                  size='small'
                                  onClick={handleEditCancel}
                                  sx={{
                                    minWidth: 'auto',
                                    padding: '2px 4px',
                                    height: '20px',
                                    fontSize: '10px',
                                    '& .tabler-square-x': {
                                      color: '#FF4C51 !important'
                                    }
                                  }}
                                >
                                  <i className='tabler-square-x text-xl' />
                                </Button>
                              </div>
                            </div>
                          </>
                        ) : (
                          <TextField
                            autoFocus
                            value={editCustomRate}
                            onChange={e => handleEditCustomRateChange(e.target.value)}
                            size='small'
                            type='number'
                            placeholder='0'
                            className='w-full h-[40px]'
                            sx={{
                              '& .MuiInputBase-root': { height: '40px', borderRadius: '20px' },
                              '& input[type=number]': { '-moz-appearance': 'textfield' },
                              '& input[type=number]::-webkit-outer-spin-button': { '-webkit-appearance': 'none', margin: 0 },
                              '& input[type=number]::-webkit-inner-spin-button': { '-webkit-appearance': 'none', margin: 0 },
                            }}
                            slotProps={{
                              input: {
                                endAdornment: (
                                  <InputAdornment position='end'>
                                    <Button
                                      size='small'
                                      onClick={() => handleEditSave(id)}
                                      sx={{
                                        minWidth: 'auto',
                                        padding: '2px 4px',
                                        height: '20px',
                                        fontSize: '10px',
                                        '& .tabler-square-check': {
                                          color: '#28C76F !important'
                                        }
                                      }}
                                    >
                                      <i className='tabler-square-check text-xs' />
                                    </Button>
                                    <Button
                                      color='error'
                                      size='small'
                                      onClick={handleEditCancel}
                                      sx={{
                                        minWidth: 'auto',
                                        padding: '2px 4px',
                                        height: '20px',
                                        fontSize: '10px',
                                        '& .tabler-square-x': {
                                          color: '#FF4C51 !important'
                                        }
                                      }}
                                    >
                                      <i className='tabler-square-x text-xs' />
                                    </Button>
                                    <Button
                                      variant='text'
                                      size='small'
                                      onClick={handleEditBackToPredefined}
                                      sx={{ minWidth: 'auto', padding: '2px', height: '20px' }}
                                    >
                                      <i
                                        className='tabler-arrow-left text-xs'
                                        style={{
                                          color: '#666 !important'
                                        }}
                                      />
                                    </Button>
                                  </InputAdornment>
                                )
                              }
                            }}
                          />
                        )}
                      </Box>
                    ) : (
                      <Box className='flex items-center justify-between'>
                        <Typography variant='body2' color='text.primary'>
                          {row?.eligibility_rate}%
                        </Typography>
                        <Button
                          variant='text'
                          size='small'
                          onClick={() => handleEditClick(id, row?.eligibility_rate || 60)}
                          sx={{ minWidth: 'auto', height: '20px' }}
                        >
                          <i
                            className='tabler-edit text-[16px]'
                            style={{
                              color: '#666'
                            }}
                          />
                        </Button>
                      </Box>
                    )}
                  </td>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant='body2' color='text.primary'>
                      {row?.birth_date
                        ? dayjs(row.birth_date).format('DD/MM/YYYY')
                        : '--'}
                    </Typography>
                  </td>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant='body2' color='text.primary'>
                      {row?.eligibility_updated_at
                        ? dayjs(row.eligibility_updated_at).format('DD/MM/YYYY')
                        : dayjs(row.created_at).format('DD/MM/YYYY')}
                    </Typography>
                  </td>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                    <Typography variant='body2' color='text.primary'>
                      {row?.joined_date
                        ? dayjs(row?.joined_date).format('DD/MM/YYYY')
                        : '-'
                      }
                    </Typography>
                  </td>
                  <td style={{ padding: '10px 16px', borderBottom: '1px solid #e0e0e0' }}>
                  <Chip
                    label={
                      row?.status === UserStatusEnum.WAITING
                        ? 'Pending'
                        : STATUS_LABELS[row?.status as keyof typeof STATUS_LABELS] || '-'
                    }
                    size="small"
                    className="capitalize px-4 py-4"
                    classes={
                      row?.status === UserStatusEnum.ACTIVE
                        ? {
                            root: 'bg-[#28C76F29] text-white rounded-[12px]',
                            label: 'text-[#28C76F]'
                          }
                        : row?.status === UserStatusEnum.DISABLE
                        ? {
                            root: 'bg-[#2F2B3D14] text-gray-600 border-gray-200 rounded-[12px]',
                            label: 'text-gray-500'
                          }
                        : row?.status === UserStatusEnum.BLOCKED
                        ? {
                            root: 'bg-[#ff4c5129] text-[#FF4C51] rounded-[12px]',
                            label: 'text-[#ff4c51]'
                          }
                        : row?.status === UserStatusEnum.WAITING
                        ? {
                            root: 'bg-[#FF9F4329] text-[#FF9F43] rounded-[12px]',
                            label: 'text-[#FF9F43]'
                          }
                          : row?.status === UserStatusEnum.WAITING_DELETE
                        ? {
                            root: 'bg-[#FF4C5129] text-[#FF4C51] rounded-[12px]',
                            label: 'text-[#FF4C51]'
                          }
                        : {
                            root: 'bg-gray-50 text-gray-600 border-gray-200 rounded-[12px]',
                            label: 'text-gray-600'
                          }
                    }
                  />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </Box>

      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
    </>
  )
}
